import { CaretSortIcon } from '@radix-ui/react-icons';
import { Language, Provider, UpdateProfileParams } from '@zond/api-interfaces';
import {
  Alert,
  AlertDescription,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@zond/ui';
import { cn } from '@zond/utils';
import { AlertTriangle, Camera, Check, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import timezones from 'timezones-list';
import { SUPPORTED_LANGUAGES } from '../../i18n/language';
import { useLanguage } from '../../i18n/language-provider';
import { useUpdatePassword } from '../auth/auth.queries';
import { useMe, useUpdateAvatar, useUpdateProfile } from '../users/user.queries';

export const Profile = () => {
  const { t } = useTranslation('settings');
  const { language, setLanguage } = useLanguage();
  const { data: user, refetch } = useMe();
  const [isUploading, setIsUploading] = useState(false);

  const [formData, setFormData] = useState({
    firstname: user?.firstname || '',
    lastname: user?.lastname || '',
    displayname: user?.displayname || '',
    email: user?.email || '',
    language: user?.language || Language.English,
    timezone: user?.timezone || 'UTC',
  });

  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: '',
  });

  const [passwordErrors, setPasswordErrors] = useState({
    password: '',
    confirmPassword: '',
  });

  const updateProfile = useUpdateProfile(
    () => {
      refetch();
      toast.success(t('profile.toasts.profileUpdated'));
    },
    () => {
      toast.error(t('profile.toasts.profileUpdateFailed'));
    }
  );

  const updatePassword = useUpdatePassword();

  const updateAvatar = useUpdateAvatar(
    () => {
      refetch();
      toast.success(t('profile.toasts.avatarUpdated'));
      setIsUploading(false);
    },
    () => {
      toast.error(t('profile.toasts.avatarUpdateFailed'));
      setIsUploading(false);
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile.mutate(formData);
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSelectChange = (field: string) => (value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validatePassword = () => {
    const errors = {
      password: '',
      confirmPassword: '',
    };

    if (!passwordData.password) {
      errors.password = t('profile.validation.passwordRequired');
    } else if (passwordData.password.length < 6) {
      errors.password = t('profile.validation.passwordMin');
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = t('profile.validation.confirmRequired');
    } else if (passwordData.password !== passwordData.confirmPassword) {
      errors.confirmPassword = t('profile.validation.passwordsNoMatch');
    }

    setPasswordErrors(errors);
    return !errors.password && !errors.confirmPassword;
  };

  const handlePasswordChange =
    (field: 'password' | 'confirmPassword') => (e: React.ChangeEvent<HTMLInputElement>) => {
      setPasswordData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
      // Clear errors when typing
      setPasswordErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    };

  const handlePasswordUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (validatePassword()) {
      updatePassword.mutate(
        {
          password: passwordData.password,
          passwordConfirmation: passwordData.confirmPassword,
        },
        {
          onSuccess: () => {
            toast.success(t('profile.toasts.passwordUpdated'));
            setPasswordData({ password: '', confirmPassword: '' });
          },
          onError: () => {
            toast.error(t('profile.toasts.passwordUpdateFailed'));
          },
        }
      );
    }
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 1000000) {
      toast.error(t('profile.toasts.avatarTooLarge'));
      return;
    }

    setIsUploading(true);
    updateAvatar.mutate(file);
  };

  return (
    <div className="container max-w-4xl py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">{t('profile.title')}</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="group relative">
                    <Avatar className="h-10 w-10 rounded-lg">
                      <AvatarImage src={user?.avatar ?? undefined} className="rounded-lg" />
                      <AvatarFallback className="rounded-lg">
                        <span role="img" aria-label="profile" className="text-2xl">
                          🐣
                        </span>
                      </AvatarFallback>
                    </Avatar>
                    <label
                      className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      htmlFor="avatar-upload"
                    >
                      {isUploading ? (
                        <Loader2 className="h-6 w-6 text-white animate-spin" />
                      ) : (
                        <Camera className="h-6 w-6 text-white" />
                      )}
                    </label>
                    <input
                      type="file"
                      id="avatar-upload"
                      className="hidden"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      disabled={isUploading}
                    />
                  </div>
                  <div>
                    <CardTitle>{t('profile.info.title')}</CardTitle>
                    <CardDescription>{t('profile.info.description')}</CardDescription>
                  </div>
                </div>
                <Button type="submit" disabled={updateProfile.isPending} className="min-w-[120px]">
                  {updateProfile.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common:states.saving')}
                    </>
                  ) : (
                    t('common:actions.saveChanges')
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="firstname">{t('profile.fields.firstname')}</Label>
                  <Input
                    id="firstname"
                    value={formData.firstname}
                    onChange={handleInputChange('firstname')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastname">{t('profile.fields.lastname')}</Label>
                  <Input id="lastname" value={formData.lastname} onChange={handleInputChange('lastname')} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="displayname">{t('profile.fields.displayname')}</Label>
                  <Input
                    id="displayname"
                    value={formData.displayname}
                    onChange={handleInputChange('displayname')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t('profile.fields.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="language">{t('profile.fields.language')}</Label>
                  <Select
                    value={language}
                    onValueChange={(value) => {
                      setLanguage(value as Language);
                      handleSelectChange('language')(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('profile.language.placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {SUPPORTED_LANGUAGES.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">{t('profile.fields.timezone')}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className={cn(
                          'w-full justify-between font-normal',
                          'bg-background px-3 py-2 h-9 border border-input',
                          'ring-offset-background',
                          'hover:bg-accent hover:text-accent-foreground',
                          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                          !formData.timezone && 'text-muted-foreground'
                        )}
                      >
                        {formData.timezone
                          ? timezones.find((tz) => tz.tzCode === formData.timezone)?.label
                          : t('profile.timezone.placeholder')}
                        <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0">
                      <Command className="w-full">
                        <CommandInput
                          placeholder={t('profile.timezone.search')}
                          className="h-9 border-none focus:ring-0"
                        />
                        <CommandEmpty>{t('profile.timezone.empty')}</CommandEmpty>
                        <CommandGroup className="max-h-64 overflow-auto">
                          {timezones.map((tz) => (
                            <CommandItem
                              key={tz.tzCode}
                              value={tz.tzCode}
                              onSelect={() => {
                                handleSelectChange('timezone')(tz.tzCode);
                              }}
                              className="cursor-pointer py-2"
                            >
                              {tz.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('profile.password.title')}</CardTitle>
                  <CardDescription>{t('profile.password.description')}</CardDescription>
                </div>
                <Button
                  variant="outline"
                  onClick={handlePasswordUpdate}
                  type="button"
                  disabled={updatePassword.isPending}
                >
                  {updatePassword.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common:states.updating')}
                    </>
                  ) : (
                    t('common:actions.update')
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="password">{t('profile.password.password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={passwordData.password}
                    onChange={handlePasswordChange('password')}
                  />
                  {passwordErrors.password && (
                    <p className="text-sm text-destructive mt-1">{passwordErrors.password}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('profile.password.confirm')}</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange('confirmPassword')}
                  />
                  {passwordErrors.confirmPassword && (
                    <p className="text-sm text-destructive mt-1">{passwordErrors.confirmPassword}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-destructive">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-destructive">{t('profile.delete.title')}</CardTitle>
                  <CardDescription>{t('profile.delete.description')}</CardDescription>
                </div>
                <Button variant="destructive">{t('profile.delete.button')}</Button>
              </div>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{t('profile.delete.ownerWarning')}</AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
};

export default Profile;
